import { useState } from 'react'
import Head from 'next/head'
import { paths } from '@mc/paths'
import { useStaticData } from '@mc/static-data-react'
import {
  CERTIFICATES,
  CertificateSlugProvider,
  getLatestCertificates,
  useIsEnterpriseMember,
} from 'pages/certificates/common'
import { BaseProviders } from 'pages/certificates/common/BaseProviders'
import { Layout } from 'pages/certificates/common/Layout'
import {
  Footer,
  PreEnrolledNavigation,
  StickyFooter,
  TrackingRow,
  TrackingRowProvider,
} from 'pages/certificates/components'
import { PageSources } from 'pages/certificates/events'
import { useCohortUrgencyCopy } from 'pages/certificates/experiments/mccerts-3290_v1_cohort_urgency'
import {
  TrackGoldStandardPageViewed,
  TrackingAttributes,
} from 'pages/certificates/tracking'
import type { CohortPageProps } from './getCohortPageProps'
import { CohortBenefits } from './hero/CohortBenefits'
import { CohortHero } from './hero/CohortHero'
import { CohortClosingCta } from './sections/CohortClosingCta'
import { CohortCurriculum } from './sections/CohortCurriculum'
import { CohortFaq } from './sections/CohortFaq'
import { CohortFeatures } from './sections/CohortFeatures'
import { CohortInstructors } from './sections/CohortInstructors'
import { CohortNotifyModal } from './sections/CohortNotifyModal'
import { CohortPartners } from './sections/CohortPartners'
import { CohortSyllabusCapture } from './sections/CohortSyllabusCapture'
import { CohortTestimonials } from './sections/CohortTestimonials'
import { getUrgencyFooterCopy } from './urgency/getUrgencyFooterCopy'
import { useHasJoinedCohort } from './urgency/useHasJoinedCohort'

const CohortLandingPageContent = ({ cohort, enrollment }: CohortPageProps) => {
  const { isLoggedIn } = useStaticData<{ isLoggedIn: boolean }>()
  const [isNotifyOpen, setIsNotifyOpen] = useState(false)
  const openNotify = () => setIsNotifyOpen(true)
  const urgencyCopy = useCohortUrgencyCopy()
  const hasJoined = useHasJoinedCohort()
  const isEnterprise = useIsEnterpriseMember()
  const stickyFooterCopy =
    hasJoined || isEnterprise
      ? null
      : getUrgencyFooterCopy(urgencyCopy.footer, cohort.config, enrollment)

  return (
    <>
      <Head>
        <meta name='robots' content='noindex' />
      </Head>
      <TrackGoldStandardPageViewed
        name={PageSources.CERTIFICATE_COHORT_LANDING}
        product='certificates'
        product_name={cohort.config.name}
      />
      <TrackingAttributes
        page_source={PageSources.CERTIFICATE_COHORT_LANDING}
        product_name={cohort.config.name}
      >
        <TrackingRowProvider event='certificateRowViewed'>
          <Layout backgroundColor='var(--c-beige-600)'>
            <TrackingAttributes location='nav bar'>
              <PreEnrolledNavigation
                topics={CERTIFICATES}
                latestCertificates={getLatestCertificates()}
                currentCertificateTitle={cohort.config.name}
                redirectTo={paths.certificates}
              />
            </TrackingAttributes>
            <div
              data-testid='cohort-landing-page'
              data-cohort-slug={cohort.config.slug}
              data-enrollment-state={enrollment.state}
            >
              <TrackingRow row_name='hero' location='hero'>
                <CohortHero
                  config={cohort.config}
                  content={cohort.content}
                  enrollment={enrollment}
                  onNotify={openNotify}
                />
              </TrackingRow>
              <TrackingRow row_name='essentials' location='essentials'>
                <CohortBenefits benefits={cohort.content.benefits} />
              </TrackingRow>
              <TrackingRow
                row_name='meet-your-instructor'
                location='meet-your-instructor'
                visibilityConfig={{ threshold: 0.2 }}
              >
                <CohortInstructors instructors={cohort.content.instructors} />
              </TrackingRow>
              <TrackingRow
                row_name='cohort-partners'
                location='cohort-partners'
              >
                <CohortPartners partners={cohort.content.partners} />
              </TrackingRow>
              <TrackingRow
                row_name='cohort-curriculum'
                location='cohort-curriculum'
                visibilityConfig={{ threshold: 0.2 }}
              >
                <CohortCurriculum curriculum={cohort.content.curriculum} />
              </TrackingRow>
              <TrackingRow
                row_name='syllabus-banner'
                location='syllabus-banner'
              >
                <CohortSyllabusCapture
                  slug={cohort.config.slug}
                  syllabusCapture={cohort.content.syllabusCapture}
                />
              </TrackingRow>
              <TrackingRow
                row_name='certificate-learning-experience'
                location='certificate-learning-experience'
                visibilityConfig={{ threshold: 0.2 }}
              >
                <CohortFeatures features={cohort.content.features} />
              </TrackingRow>
              <TrackingRow
                row_name='enroll-prompt'
                location='enroll-prompt'
                visibilityConfig={{ threshold: 0.2 }}
              >
                <CohortClosingCta
                  config={cohort.config}
                  content={cohort.content}
                  enrollment={enrollment}
                  onNotify={openNotify}
                />
              </TrackingRow>
              <CohortTestimonials testimonials={cohort.content.testimonials} />
              <TrackingRow
                row_name='FAQs'
                visibilityConfig={{ threshold: 0.2 }}
              >
                <CohortFaq faq={cohort.content.faq} />
              </TrackingRow>
            </div>
            {stickyFooterCopy && (
              <CertificateSlugProvider value={{ certSlug: cohort.config.slug }}>
                <StickyFooter
                  variant='default'
                  footer={{ text: stickyFooterCopy.headline }}
                  subText={stickyFooterCopy.subline}
                  ctaText={cohort.content.hero.ctaLabel}
                />
              </CertificateSlugProvider>
            )}
            <CohortNotifyModal
              slug={cohort.config.slug}
              show={isNotifyOpen}
              onClose={() => setIsNotifyOpen(false)}
            />
            <TrackingRow
              row_name='footer'
              visibilityConfig={{ threshold: 0.01 }}
            >
              <TrackingAttributes location='footer'>
                <Footer isLoggedIn={isLoggedIn} />
              </TrackingAttributes>
            </TrackingRow>
          </Layout>
        </TrackingRowProvider>
      </TrackingAttributes>
    </>
  )
}

export const CohortLandingPage = (props: CohortPageProps) => (
  <BaseProviders>
    <CohortLandingPageContent {...props} />
  </BaseProviders>
)
